"use client";

import { ArrowRight, ChevronDown } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function PremiumConstructionSolutionsHero() {
  const word1 = useRevealOnScroll<HTMLSpanElement>({
    threshold: 0.1
  });
  const word2 = useRevealOnScroll<HTMLSpanElement>({
    threshold: 0.1
  });
  const word3 = useRevealOnScroll<HTMLSpanElement>({
    threshold: 0.1
  });
  const paragraph = useRevealOnScroll<HTMLParagraphElement>({
    threshold: 0.1
  });
  const actions = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const image = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.05
  });
  const credit = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.05
  });
  const scrollIndicator = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.05
  });
  return <section data-section-id="4022" id="premium-construction-solutions-hero" className="relative bg-background text-foreground overflow-hidden min-h-screen w-full">
      <div className="relative mx-auto max-w-[1600px] px-6 sm:px-8 lg:px-12 pt-24 pb-16 lg:pt-32 lg:pb-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-8 items-start">
          {}
          <div className="lg:col-span-5 lg:pt-8 order-2 lg:order-1 flex flex-col">
            {}
            <h1 className="font-default font-semibold leading-[0.95] tracking-tighter text-foreground text-5xl sm:text-6xl md:text-7xl lg:text-7xl xl:text-8xl 2xl:text-9xl">
              <span ref={word1.ref} className={`block transition-all duration-700 ease-out ${word1.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
                Premium{" "}
                <span style={{
                color: "var(--primary)"
              }} className="font-semibold">
                  /
                </span>
              </span>
              <span ref={word2.ref} className={`block italic transition-all duration-700 ease-out delay-150 ${word2.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
                construction{" "}
                <span style={{
                color: "var(--primary)"
              }} className="not-italic font-semibold">
                  /
                </span>
              </span>
              <span ref={word3.ref} className={`block transition-all duration-700 ease-out delay-300 ${word3.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
                materials.
              </span>
            </h1>

            {}
            <p ref={paragraph.ref} className={`mt-10 max-w-md font-default text-lg lg:text-xl text-foreground/80 leading-relaxed transition-all duration-700 ease-out delay-500 ${paragraph.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
              Direct sourcing of high-quality construction materials — sandwich panels, steel sheets, facade systems, aluminium systems, steel buildings and more — for B2B and B2C clients across the Balkans and Europe
            </p>

            {}
            <div ref={actions.ref} className={`mt-10 flex flex-wrap items-center gap-6 transition-all duration-700 ease-out delay-700 ${actions.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
              <Button asChild size="lg" className="px-8 h-12 bg-primary text-primary-foreground hover:bg-primary/90 font-default text-sm tracking-wide">
                <Link to="/ProductsPage">View Our Products</Link>
              </Button>
              <Link to="/AboutUsPage" className="group inline-flex items-center gap-2 font-default text-foreground hover:text-primary transition-colors duration-300">
                <span className="border-b border-foreground/30 group-hover:border-primary pb-1">
                  Learn About Us
                </span>
                <ArrowRight className="size-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
            </div>
          </div>

          {}
          <div className="lg:col-span-7 order-1 lg:order-2 relative">
            <div ref={image.ref} className={`relative overflow-hidden transition-all duration-1000 ease-out ${image.isVisible ? "opacity-100 scale-100" : "opacity-0 scale-[0.98]"}`} style={{
            aspectRatio: "4 / 5"
          }}>
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1699139/img/industrial_sandwich_panels_warehouse.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1699139/img/industrial_sandwich_panels_warehouse.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1699139/img/industrial_sandwich_panels_warehouse.png-wvc-sizes" alt="High-quality sandwich panels in a modern industrial warehouse" className="w-full h-full object-cover" loading="eager" decoding="async" />
              {}
              <div className="absolute inset-0 bg-gradient-to-t from-foreground/20 via-transparent to-transparent pointer-events-none" />
            </div>

            {}
            <div ref={credit.ref} className={`absolute -bottom-6 -left-4 sm:left-6 lg:-left-12 lg:-bottom-8 max-w-xs bg-card text-card-foreground border border-border p-5 sm:p-6 transition-all duration-1000 ease-out delay-500 ${credit.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="font-default text-lg sm:text-xl text-foreground leading-tight">
                    Industrial Grade Steel
                  </p>
                  <p className="mt-2 font-mono text-[10px] sm:text-xs tracking-[0.18em] uppercase text-muted-foreground">
                    Bujanovac, Serbia — 2025
                  </p>
                </div>
                <div className="size-2 shrink-0" style={{
                backgroundColor: "var(--primary)"
              }} />
              </div>
            </div>
          </div>
        </div>

        {}
        <div ref={scrollIndicator.ref} className={`hidden lg:flex absolute bottom-8 left-12 flex-col items-center gap-3 transition-all duration-1000 ease-out delay-1000 ${scrollIndicator.isVisible ? "opacity-100" : "opacity-0"}`}>
          <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-muted-foreground" style={{
          writingMode: "vertical-rl"
        }}>
            Scroll
          </span>
          <div className="relative h-16 w-px bg-foreground/20 overflow-hidden">
            <div className="absolute top-0 left-0 w-full h-6 bg-primary" style={{
            animation: "scrollLine 2s ease-in-out infinite"
          }} />
          </div>
          <ChevronDown className="size-3 text-primary" style={{
          animation: "scrollBounce 2s ease-in-out infinite"
        }} />
        </div>
      </div>

      <style>{`
        @keyframes scrollLine {
          0% { transform: translateY(-100%); }
          100% { transform: translateY(400%); }
        }
        @keyframes scrollBounce {
          0%, 100% { transform: translateY(0); opacity: 0.5; }
          50% { transform: translateY(4px); opacity: 1; }
        }
      `}</style>
    </section>;
}